import type { Booking, BookingGarment, BookingWearer, PrintLetterhead } from '../api/bookings';
import { absent, formatDocumentDate } from '../format/dates';
import { formatMoney } from '../format/money';
import { encodeQr, fits } from '../format/qr';
import type { DocketLanguage } from '../generated/enums';

import { measureLabel } from '../bookings/alterations';
import { withReference } from '../units/referenceNumber';
import { alterationFigure, alterationNarrative } from './alterationNarrative';
import { docketLines, docketLinesFor } from './docket';
import { languageNames, languageTags, translationsReviewed } from './narrativeText';
import { escapeHtml, mmToPt, qrSvg, type PrintPage } from './printHtml';

/**
 * The tailor's docket as an A5 page — what the phone sends on WhatsApp and hands to its print dialog
 * (spec §11), sheet for sheet the website's `AlterationDocket`.
 *
 * <p>
 * <strong>One sheet per garment, in its own tailor's language</strong> unless the print says
 * otherwise, with every heading naming that garment's own tailor and due date; the QR is the
 * <em>garment's</em> code, so scanning closes the job against the right physical garment; the
 * reversible-or-permanent banner carries the garment's value; and the blank box is for the tailor
 * to write back into. <strong>Never A4</strong>, the docket's rule, and on the phone never a roll
 * either: a roll there is a fixed-column layout, and a Gujarati sentence cannot be padded into
 * columns.
 * </p>
 */

export interface DocketInput {
  booking: Booking;
  letterhead: PrintLetterhead;
  /** One garment's sheet; absent prints every garment with work on it. */
  garmentId?: number | undefined;
  /** Overrides each sheet's own tailor language, for this print only. */
  language?: DocketLanguage | undefined;
}

const e = escapeHtml;

/** The garments this print has sheets for — none is a print with nothing on it. */
export function docketSheets(input: DocketInput) {
  return input.garmentId === undefined
    ? docketLines(input.booking)
    : docketLinesFor(input.booking, input.garmentId);
}

export function docketPage(input: DocketInput): PrintPage {
  const lines = docketSheets(input);
  const width = 148;
  const height = 210;
  const margin = 10;

  const css = `
    @page { size: A5; margin: ${margin}mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { color: #111; font-family: 'Noto Sans', 'Noto Sans Gujarati', 'Noto Sans Devanagari',
           Roboto, Arial, sans-serif; font-size: 9pt; line-height: 1.45; }
    .sheet { break-after: page; page-break-after: always; }
    .sheet:last-child { break-after: auto; page-break-after: auto; }
    .head { display: flex; justify-content: space-between; gap: 10pt; align-items: flex-start; }
    .title { font-weight: 700; font-size: 11pt; }
    .muted { color: #5b6570; }
    .cap { font-size: 7.5pt; letter-spacing: 1.2pt; text-transform: uppercase; color: #5b6570; }
    hr { border: 0; border-top: 0.75pt solid #1f2933; margin: 8pt 0; }
    hr.thin { border-top: 0.5pt solid #c9ced6; }
    dl { display: grid; grid-template-columns: 22mm 1fr; gap: 1pt 8pt; margin: 0; }
    dt { color: #5b6570; } dd { margin: 0; }
    .due { border: 1.5pt solid #1f2933; border-radius: 3pt; padding: 5pt 8pt; margin: 8pt 0;
           display: flex; justify-content: space-between; align-items: baseline; }
    .due b { font-size: 11pt; }
    .item { display: flex; gap: 10pt; align-items: flex-start; margin-top: 6pt; }
    .qr { width: 24mm; height: 24mm; display: block; }
    .code { font-family: Menlo, 'Courier New', monospace; font-size: 7.5pt; text-align: center; }
    .work { border-left: 2.5pt solid #1f2933; padding: 2pt 0 2pt 9pt; font-size: 10pt;
            line-height: 1.6; margin: 2pt 0 0; white-space: pre-line; }
    table { width: 100%; border-collapse: collapse; margin-top: 6pt; }
    th { text-align: left; font-size: 7.5pt; letter-spacing: 0.8pt; text-transform: uppercase;
         color: #5b6570; border-bottom: 0.5pt solid #c9ced6; padding: 2pt 0; }
    td { border-bottom: 0.5pt solid #c9ced6; padding: 3pt 0; vertical-align: top; }
    .num { text-align: right; font-weight: 700; white-space: nowrap; padding-right: 8pt; }
    .box { display: inline-block; width: 9pt; height: 9pt; border: 0.75pt solid #1f2933; }
    .banner { border: 0.75pt solid #1f2933; border-radius: 3pt; padding: 5pt 8pt; margin-top: 6pt; }
    .row { display: flex; justify-content: space-between; gap: 10pt; margin-top: 6pt; }
    .reply { border: 0.75pt dashed #1f2933; border-radius: 3pt; padding: 6pt 8pt; margin-top: 8pt;
             color: #5b6570; }
    .reply p { display: flex; gap: 8pt; margin: 0 0 6pt; }
    .reply p:last-child { margin: 0; }
    .line { flex: 1; border-bottom: 0.75pt solid #1f2933; height: 11pt; }
    @media screen { body { width: ${width}mm; padding: ${margin}mm; } }`;

  const body = lines
    .map((line, index) =>
      sheet(input, line.garment, line.wearer, index + 1, lines.length, input.language),
    )
    .join('');

  const viewportPx = Math.round((width / 25.4) * 96);

  return {
    html: `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=${viewportPx}"><title>Alteration docket</title><style>${css}</style></head><body>${body}</body></html>`,
    widthPt: mmToPt(width),
    heightPt: mmToPt(height),
  };
}

function sheet(
  input: DocketInput,
  garment: BookingGarment,
  wearer: BookingWearer,
  number: number,
  of: number,
  override: DocketLanguage | undefined,
): string {
  const { booking, letterhead } = input;
  const language = override ?? garment.alterationTailorLanguage ?? 'English';
  const currency = letterhead.currencySymbol;
  const narrative = alterationNarrative(garment.alterations, language);
  const qr = fits(garment.serialCode) ? qrSvg(encodeQr(garment.serialCode)) : '';

  const rows = garment.alterations
    .map(
      (detail) =>
        `<tr><td><span class="box"></span></td><td>${e(measureLabel(detail.measure))}</td><td class="num">${e(alterationFigure(detail))}</td><td class="muted">${e(detail.note ?? '')}</td></tr>`,
    )
    .join('');

  const banner = garment.alterationIsReversible
    ? `<div class="banner"><b>⚠ REVERSIBLE — fitting alteration, not a repair.</b> Keep all cut cloth and the original thread with the garment. This is to be let back out when it returns from this rental. <b>Do not trim excess</b>${garment.replacementValue === null ? '.' : ` — this garment is worth ${e(currency)}${e(formatMoney(garment.replacementValue))}.`}</div>`
    : `<div class="banner"><b>PERMANENT REPAIR.</b> Restore the garment to rentable condition. This alteration is not reversed afterwards.</div>`;

  return `<section class="sheet">
    <div class="head"><div><div class="title">ALTERATION DOCKET${garment.alterationJobNumber ? ` <span style="font-family: monospace">${e(garment.alterationJobNumber)}</span>` : ''}</div><div class="cap">Internal — not for the customer</div></div><div class="muted" style="text-align: right">${e(`${letterhead.tenantName} · ${letterhead.branchName}`)}</div></div>
    <hr>
    <dl><dt>Tailor</dt><dd><b>${e(garment.alterationTailorName || 'Unassigned')}</b></dd><dt>Booking</dt><dd>${e(`${booking.documentNumber} · ${booking.customerName} · ${booking.customerMobile}`)}</dd><dt>Event</dt><dd>${e(formatDocumentDate(booking.eventDate))}</dd></dl>
    <div class="due"><span class="cap" style="color: #111; font-weight: 700">Must be back by</span><b>${e(formatDocumentDate(garment.alterationDueOn))}</b></div>
    <hr class="thin">
    <div class="head"><b>${e(`${number} · ${garment.itemName}`)}</b><span class="muted">${e(garment.variantDisplayName)}</span></div>
    <div class="item"><div>${qr}<div class="code">${e(withReference(garment.serialCode, garment.referenceNumber))}</div></div><dl style="flex: 1"><dt>Wearer</dt><dd><b>${e(wearer.name)}</b>${wearer.relation ? e(` — ${wearer.relation}`) : ''}</dd><dt>Measured</dt><dd>${e(formatDocumentDate(wearer.fittingCompletedOn))}</dd></dl></div>
    <div class="cap" style="margin-top: 8pt; font-weight: 700">Work to do</div>
    <p class="work" lang="${languageTags[language]}">${e(narrative)}</p>
    ${!translationsReviewed && language !== 'English' ? `<div class="muted" style="font-size: 7.5pt; margin-top: 3pt">⚠ ${e(languageNames[language])} wording is not yet checked against a tailor.</div>` : ''}
    ${rows ? `<table><thead><tr><th style="width: 12pt">✓</th><th>What</th><th style="text-align: right; padding-right: 8pt">Inches</th><th>Note</th></tr></thead><tbody>${rows}</tbody></table>` : ''}
    ${banner}
    <div class="row"><span>Charge to job: <b>${garment.alterationCharge === null ? absent : e(`${currency}${formatMoney(garment.alterationCharge)}`)}</b></span><span>Due back: <b>${e(formatDocumentDate(garment.alterationDueOn))}</b></span></div>
    <div class="reply"><p><span>Started</span><span class="line"></span><span>Finished</span><span class="line"></span></p><p><span>Work differs from the above? Y / N</span><span class="line"></span></p><p><span>Tailor signature</span><span class="line"></span></p></div>
    <hr class="thin">
    <div class="row muted" style="font-size: 7.5pt"><span>${of > 1 ? `Sheet ${number} of ${of}` : 'One garment on this docket'}</span><span>Scan the QR to close the job</span></div>
  </section>`;
}
